import { BCMSDate, BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account, PositivePayExceptionCheck } from "model";
import TASPositivePayCheckForm from "components/pages/TASPositivePayAddChecksPage/classes/TASPositivePayCheckForm";
import TASPositivePayExceptionCheckForm from "../TASPositivePayExceptionCheckForm";
/**
 * @class TASPositivePayCheckExceptionsSearch
 * @classdesc Class for controlling the search functionality of the "View Checks" page.
 * @property {ACCOUNT} _account - The account to filter by.
 * @property {ARRAY} _exceptionCheckForms - The available check forms.
 * @property {ARRAY} _editExceptionCheckForms - The available editable check forms.
 * @property {ARRAY} _accounts - The list of available accounts.
 */
export default class TASPositivePayCheckExceptionsSearch {
    constructor(record) {
        // "exceptionCheckForms" are the default, unedited check forms.
        this.exceptionCheckForms = record.exceptionCheckForms;
        // "editExceptionCheckForms" are the edited check forms since
        // the user can edit as they wish and cancel those edits too.
        this.editExceptionCheckForms = record.editExceptionCheckForms;
        this.accounts = record.accounts;
        this.account = record.account;
    }

    /**
     * @name exceptionCheckForms
     * @instance
     * @property set {ARRAY} - sets the check forms of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    set exceptionCheckForms(value) { this._exceptionCheckForms = value; }

    /**
     * @name editExceptionCheckForms
     * @instance
     * @property set {ARRAY} - sets the editable check forms of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    set editExceptionCheckForms(value) { this._editExceptionCheckForms = value; }

    /**
     * @name accounts
     * @instance
     * @property set {STRING} - sets the accounts of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    set accounts(value) { this._accounts = value; }

    /**
     * @name account
     * @instance
     * @property set {STRING} - sets the company id
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    set account(val) {
        if(val) {
            if(val.id) {
                this._account = val;
            } else if(val === "all") {
                this._account = {id: "all", value: "all", name: "All", number: "", };
            } else {
                this._account = Account.initByType(val);
            }
        } else {
            this._account = val;
        }
    }

    /**
     * @name exceptionCheckForms
     * @instance
     * @property get {ARRAY} - returns the check forms of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get exceptionCheckForms() { return this._exceptionCheckForms; }

    /**
     * @name exceptionCheckForms
     * @instance
     * @property get {ARRAY} - returns the editable check forms of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get editExceptionCheckForms() { return this._editExceptionCheckForms; }

    /**
     * @name accounts
     * @instance
     * @property get {STRING} - returns the accounts of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get accounts() { return this._accounts; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get account() { return this._account; }


    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && key !== "accounts" 
            && key !== "exceptionCheckForms" && key !== "editExceptionCheckForms") {
                result[key] = value;
            }

            return result;
        }, {});
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get record() {
        return {
            exceptionCheckForms: this.exceptionCheckForms,
            accounts: this.accounts,
            account: this.account,
        };
    }

    /**
     * @function checkValue
     * @memberof TASPositivePayCheckExceptionsSearch
     * @instance
     * @property {Object} item - The item to evaluate.
     * @property {STRING} value - The search value to compare against.
     * @description Checks whether an item passes the current search filters.
     * @returns {BOOLEAN} True if the item passes the search filters. Otherwise, false.
     */
    checkValue(item, key) {
        switch(key) {
            case "account":
                return (item.account.id === this[key].id) || this[key].id === "all";
            default:
                return true;
        }
    }

    /**
     * @function filter
     * @memberof TASPositivePayCheckExceptionsSearch
     * @instance
     * @property {VOID}
     * @description returns the check forms that have pass through the search filter.
     * @returns {ARRAY}
     */
    filter() {
        const items = this.exceptionCheckForms.filter(item => {
            if(!item.account) return false;
            return item.account.id === this.account.id || this.account.id === "all";
        });

        return items.filter(item => {
            return Object.keys(this.filters).reduce((result, key) => {
                if(!this.checkValue(item, key)) {
                    result = false;
                }

                return result;
            }, true);
        }, []);
    }

    /**
     * @function userEditedCheckForms
     * @memberof TASPositivePayCheckExceptionsSearch
     * @instance
     * @property {VOID}
     * @description returns an array of check forms that have been edited but not "saved" yet.
     * @returns {ARRAY}
     */
    userEditedCheckForms() {
        let differentItems = [];

        const addItem = item => {
            const itemAlreadyAdded = differentItems.filter(_item => _item.id === item.id).length > 0;
            if(!itemAlreadyAdded) {
                differentItems.push(item);
            }
        };
        
        for(let i = 0; i < this.exceptionCheckForms.length; i++) {
            let currCheckFormRecord = this.exceptionCheckForms[i].record;
            let currEditCheckFormRecord = this.editExceptionCheckForms.filter(item => item.id === currCheckFormRecord.id)[0].record;
            let same = true;

            Object.keys(currCheckFormRecord).map(key => {
                const currCheckFormVal = currCheckFormRecord[key];
                const currEditCheckFormVal = currEditCheckFormRecord[key];

                if(currCheckFormVal instanceof BCMSDate) {
                    const sameDay = currCheckFormVal.date.getFullYear() === currEditCheckFormVal.date.getFullYear()
                    && currCheckFormVal.date.getMonth() === currEditCheckFormVal.date.getMonth()
                    && currCheckFormVal.date.getDate() === currEditCheckFormVal.date.getDate();

                    if(!sameDay) {
                        same = false;
                    }

                } else if(currCheckFormVal instanceof BCMSCurrency) {
                    if(currCheckFormVal.amount !== currEditCheckFormVal.amount) {
                        same = false;
                    }
                } else if(key === "account") {
                    if(currCheckFormVal.id !== currEditCheckFormVal.id) {
                        same = false;
                    }
                } else if(currCheckFormVal !== currEditCheckFormVal) {
                    same = false;
                }

                if(!same) {
                    addItem(this.exceptionCheckForms[i]);
                }
            });
        }

        return differentItems;
    }
    
    get editExceptionChecksHaveReturnReasonError() {
        let hasError = false;

        for(let i = 0; i < this.editExceptionCheckForms.length; i++) {
            const currEditCheckFormRecord = this.editExceptionCheckForms[i].record;
            
            if(currEditCheckFormRecord.action === "return"
            && currEditCheckFormRecord.returnReason === ""
            && currEditCheckFormRecord.didClickReturnReason === true) {
                hasError = true;
                break;
            }
        }

        return hasError;
    }

    /**
     * @function defaultVals
     * @memberof TASPositivePayCheckExceptionsSearch
     * @static
     * @property {VOID}
     * @description returns an object of default values for this class.
     * @returns {OBJECT}
     */
    static defaultVals() {
        let accounts = [{id: "all", value: "all", name: "All", number: "", }];
        accounts = [...accounts, ...Account.initAllBusinessAssetOnly().filter(item => item.type !== "savings")];
        const selectedAccount = accounts[0] ?? {};

        const exceptionCheckForms = PositivePayExceptionCheck.initAll().reduce((acc, item) => {
            acc.push( TASPositivePayExceptionCheckForm.init({ ...item.record, action: "prompt" }));
            return acc;
        }, []);

        const editExceptionCheckForms = PositivePayExceptionCheck.initAll().reduce((acc, item) => {
            acc.push( TASPositivePayExceptionCheckForm.init({ ...item.record, action: "prompt" }));
            return acc;
        }, []);

        return { 
            exceptionCheckForms: exceptionCheckForms,
            editExceptionCheckForms: editExceptionCheckForms,
            accounts: accounts,
            account: selectedAccount,
        };
    }

    /**
     * @function reset
     * @memberof TASPositivePayCheckExceptionsSearch
     * @instance
     * @property {VOID}
     * @description returns an instance of this class with the default values.
     * @returns {TASPositivePayCheckExceptionsSearch}
     */
    reset() {
        const defaultVals = TASPositivePayCheckExceptionsSearch.defaultVals();
        return TASPositivePayCheckExceptionsSearch.init(defaultVals);
    }

    /**
     * @function resetFieldsOnly
     * @memberof TASPositivePayCheckExceptionsSearch
     * @instance
     * @property {VOID}
     * @description returns an instance of this class with some, not all, default values.
     * The check forms and editable check forms are left alone since the user may have edited and saved
     * checks, the data of which we still want to keep.
     * @returns {TASPositivePayCheckExceptionsSearch}
     */
    resetFieldsOnly() {
        const defaultVals = { 
            ...TASPositivePayCheckExceptionsSearch.defaultVals(),
            exceptionCheckForms: this.exceptionCheckForms,
            editExceptionCheckForms: this.editExceptionCheckForms
         };
       
        return TASPositivePayCheckExceptionsSearch.init(defaultVals);
    }

    /**
     * @function init
     * @memberof TASPositivePayCheckExceptionsSearch
     * @instance
     * @property {VOID}
     * @description returns an instance of this class with default values
     * that can be overridden by passing an object to the argument.
     * @returns {TASPositivePayCheckExceptionsSearch}
     */
    static init(record) {
        const defaultVals = TASPositivePayCheckExceptionsSearch.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayCheckExceptionsSearch(vals);
    }
}